import React from "react";
import { useNavigate } from "react-router-dom";
import DashboardPanel from "./DashboardPanel";

const fmtN = (n) => Number(n || 0).toLocaleString();

const formatDay = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};

// The two or three largest numbers in a log, e.g. "24 Connections Sent · 3 Screener Responses".
const summarise = (log, metrics) =>
  metrics
    .map(({ key, label }) => ({ label, value: Number(log?.[key]) || 0 }))
    .filter((m) => m.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 3)
    .map((m) => `${fmtN(m.value)} ${m.label}`)
    .join(" · ");

/**
 * Newest activity log entries. Rows open the project board the log belongs to.
 * @param {{ title: string, description?: string, logs?: object[], metrics: object[],
 *           showAuthor?: boolean, loading?: boolean, error?: boolean, emptyText: string }} props
 */
function RecentLogsCard({ title, description, logs, metrics, showAuthor = false, loading = false, error = false, emptyText }) {
  const navigate = useNavigate();

  let body;
  if (loading) {
    body = (
      <div className="space-y-4" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="space-y-1.5">
            <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200 dark:bg-slate-700" />
            <div className="h-3 w-3/4 animate-pulse rounded bg-gray-100 dark:bg-slate-700/60" />
          </div>
        ))}
      </div>
    );
  } else if (error) {
    body = <p className="text-sm text-app-error">{"Couldn't load activity logs."}</p>;
  } else if (!logs?.length) {
    body = <p className="text-sm text-app-muted">{emptyText}</p>;
  } else {
    body = (
      <ul className="-mx-2 space-y-0.5">
        {logs.map((log, i) => (
          <li key={log.id ?? i}>
            <button
              type="button"
              onClick={() =>
                navigate(`/admin/project-management/${log.project.backendId || log.project.projectId}`, {
                  state: { project: log.project },
                })
              }
              className="w-full rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-app-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-sm font-medium text-app-primary">{log.project.projectName}</p>
                <span className="shrink-0 text-xs text-app-muted">{formatDay(log.date)}</span>
              </div>
              {showAuthor && (
                <p className="truncate text-xs font-medium text-app-secondary">{log.User?.email || "—"}</p>
              )}
              <p className="truncate text-xs text-app-muted">
                {summarise(log, metrics) || "No activity counts recorded"}
              </p>
            </button>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <DashboardPanel title={title} description={description}>
      {body}
    </DashboardPanel>
  );
}

export default RecentLogsCard;
